import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom, Subject } from 'rxjs';

import { environment } from 'src/environments/environment';

// Per-category ratings the platform breaks a review down into
// (e.g. { cleanliness: 5, communication: 4 }). Shape varies by platform.
export interface GuestReviewRatings {
  [category: string]: number;
}

// Whether the host has replied to a guest review on the platform (gotidy's
// GuestReview#response_status). Unanswered reviews older than 30 days read as
// 'no_response' rather than 'needs_response'. 'failed' means the platform
// rejected our last reply (response_error says why) and it can be retried.
export type ReviewResponseStatus = 'responded' | 'needs_response' | 'no_response' | 'failed';

// Mirrors GET /api/v1/customer/guest-reviews (gotidy GuestReview entity).
// Inbound reviews are captured from the connected platform; Phase 3 adds a
// respond path (POST .../respond) that our browser automation posts to Airbnb.
export interface GuestReview {
  id: number;
  platform: string;
  guest_name: string;
  overall_rating: number;
  // The platform's overall-rating maximum: Airbnb scores out of 5, VRBO and
  // Booking.com out of 10. Absent on older responses — callers default to 5.
  rating_scale?: number;
  comment: string;
  stay_dates: string;
  listing_name: string;
  address_listing_id: number;
  ratings: GuestReviewRatings | null;
  reviewed_at: string;
  // Optional: absent from the live response until the response-status backend
  // (gotidy #8465) deploys. Callers treat a missing status as 'needs_response'.
  responded_at?: string | null;
  response_status?: ReviewResponseStatus;
  host_response?: string | null;
  response_error?: string | null;
  response_failed_at?: string | null;
  // The reply text of the last failed attempt, kept so it can be retried.
  // Never the posted reply: that is host_response.
  failed_response?: string | null;
  // false while the platform's respond write isn't live in the capture service
  // (gotidy refuses it). Absent on backends without the flag: treat as allowed.
  can_respond?: boolean;
  created_at: string;
}

export interface GuestReviewsFilter {
  addressId?: number;
  platform?: string;
}

// A checked-out reservation the host still owes a guest review for. This is the
// "outbound" side of reviews. gotidy has no outbound-review data model yet
// (Trello 18590 Phase 1), so status is a placeholder: every past checkout on a
// review-supported platform is 'needs_review' until the backend can tell us a
// review was left.
export type ExpectedReviewStatus = 'needs_review' | 'review_left';

export interface ExpectedReview {
  reservationId: number;
  listingName: string;
  guestName: string;
  platform: string;
  checkInDate: string;
  checkOutDate: string;
  status: ExpectedReviewStatus;
  // false while no leave-review write is live for the platform (gotidy refuses
  // it). Absent on backends without the flag: treat as allowed.
  canLeaveReview?: boolean;
}

// Mirrors GET /api/v1/customer/guest-reservation-reviews (gotidy #8479). `property` is a
// street address, not the listing title (no queryable listing name backend-side).
interface ExpectedReviewResponse {
  id: number;
  guest_name: string | null;
  property: string | null;
  address_id: number | null;
  platform: string | null;
  check_in_date: string | null;
  check_out_date: string | null;
  review_status?: ExpectedReviewStatus;
  can_leave_review?: boolean;
}

// Minimal shape we read off GET /api/v1/customer/guest-reservations. The
// endpoint returns much more; we only touch what the outbound view needs.
interface GuestReservationSourceGuest {
  name?: string;
  is_booker?: boolean;
  is_staying?: boolean;
}

interface GuestReservationListItem {
  id: number;
  guest_name?: string;
  check_in_date?: string;
  check_out_date?: string;
  guest_reservation_source_type?: { name?: string };
  address?: { address_name?: string; address?: string; address1?: string; zip?: string };
  // The guest's name lives here (not at the top level) — under source_data.guests,
  // with the booker flagged is_booker.
  source_data?: { guests?: GuestReservationSourceGuest[] };
}

// Airbnb asks hosts to rate departing guests on these categories (1-5). Kept
// open-ended so other platforms can add their own without a type change.
export interface HostGuestReviewRatings {
  cleanliness?: number;
  communication?: number;
  observance_of_house_rules?: number;
  [category: string]: number | undefined;
}

// Lifecycle of an outbound (host → guest) review. 'queued' negatives auto-post
// at day 14 of Airbnb's window; 'posted' has hit the platform; 'failed' carries
// an error and can be retried. Mirrors host_guest_reviews.status (gotidy).
export type HostGuestReviewStatus = 'draft' | 'queued' | 'posted' | 'failed';

// What the leave/queue form collects. The client only expresses intent
// (post now vs. queue as negative); the backend owns post_at (checkout + 13d)
// and the resulting status, matching the double-blind day-14 posting rule.
export interface HostGuestReviewInput {
  guestReservationId: number;
  ratings: HostGuestReviewRatings;
  publicText: string;
  privateNote: string;
  recommend: boolean;
  // true → queue and auto-post at day 14 (the negative path, minimizing the
  // guest's retaliation window); false → post immediately (the positive path).
  queueAsNegative: boolean;
}

// Mirrors a host_guest_reviews row (gotidy). Returned by the outbound CRUD
// endpoints so the form can render an existing draft/queued/posted review.
export interface HostGuestReview {
  id: number;
  guest_reservation_id: number;
  platform: string;
  ratings: HostGuestReviewRatings | null;
  public_text: string | null;
  private_note: string | null;
  recommend: boolean | null;
  status: HostGuestReviewStatus;
  post_at: string | null;
  posted_at: string | null;
  platform_review_id?: string | null;
  error?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class PropertyReviewsService {

  private http = inject(HttpClient);

  // Platforms whose reviews TIDY ingests (gotidy reviews_supported: airbnb, vrbo,
  // booking_com). Used by the client-side expected-reviews fallback to keep only
  // checked-out reservations on a review-supported source, and to derive the
  // platform slug from the source-type name. `match` is a lowercase substring of
  // the guest_reservation_source_type name; `slug` is the canonical platform value.
  private static readonly REVIEWS_SUPPORTED_PLATFORMS: ReadonlyArray<{ slug: string; match: string }> = [
    { slug: 'airbnb', match: 'airbnb' },
    { slug: 'vrbo', match: 'vrbo' },
    { slug: 'booking_com', match: 'booking' },
  ];

  // Which list a write changed. On mobile the review pages are routes, not a panel, so the
  // Reviews tab has no close to react to and reloads when this fires instead.
  private reviewsChanged = new Subject<'inbound' | 'expected'>();
  reviewsChanged$ = this.reviewsChanged.asObservable();

  async getGuestReviews(filters: GuestReviewsFilter = {}): Promise<GuestReview[]> {
    let params = new HttpParams();
    if (filters.addressId !== undefined) {
      params = params.set('address_id', filters.addressId);
    }
    if (filters.platform) {
      params = params.set('platform', filters.platform);
    }
    return firstValueFrom(
      this.http.get<GuestReview[]>(`${environment.api_url}api/v1/customer/guest-reviews`, { params })
    );
  }

  // Outbound "expected reviews": prefer the dedicated endpoint (gotidy #8479),
  // which carries the guest name. While it rolls out, any failure (e.g. 404 on
  // envs where it isn't deployed) falls back to deriving the list client-side
  // from reservations, so the tab keeps working throughout the migration.
  async getExpectedReviews(filters: GuestReviewsFilter = {}): Promise<ExpectedReview[]> {
    try {
      return await this.fetchExpectedReviews(filters);
    } catch {
      return this.deriveExpectedReviewsFromReservations(filters);
    }
  }

  private async fetchExpectedReviews(filters: GuestReviewsFilter): Promise<ExpectedReview[]> {
    let params = new HttpParams().set('per_page', '200');
    if (filters.addressId !== undefined) {
      params = params.set('address_id', filters.addressId);
    }
    const rows = await firstValueFrom(
      this.http.get<ExpectedReviewResponse[]>(`${environment.api_url}api/v1/customer/guest-reservation-reviews`, { params })
    );
    return (rows || []).map(row => this.fromExpectedReviewResponse(row));
  }

  private fromExpectedReviewResponse(row: ExpectedReviewResponse): ExpectedReview {
    return {
      reservationId: row.id,
      listingName: row.property || '',
      guestName: row.guest_name || '',
      platform: row.platform || 'airbnb',
      checkInDate: row.check_in_date || '',
      checkOutDate: row.check_out_date || '',
      status: row.review_status || 'needs_review',
      canLeaveReview: row.can_leave_review,
    };
  }

  // Fallback: fetch reservations, keep review-supported ones already checked out,
  // and mark each 'needs_review'. Covers Airbnb, VRBO and Booking.com, matching
  // the backend gate (reviews_supported).
  private async deriveExpectedReviewsFromReservations(filters: GuestReviewsFilter): Promise<ExpectedReview[]> {
    let params = new HttpParams().set('per_page', '200');
    if (filters.addressId !== undefined) {
      params = params.set('address_id', filters.addressId);
    }
    const reservations = await firstValueFrom(
      this.http.get<GuestReservationListItem[]>(`${environment.api_url}api/v1/customer/guest-reservations`, { params })
    );
    const now = Date.now();
    return (reservations || [])
      .map(reservation => ({ reservation, platform: this.reviewPlatformOf(reservation) }))
      .filter(({ reservation, platform }) => platform !== null && this.hasCheckedOut(reservation, now))
      .map(({ reservation, platform }) => this.toExpectedReview(reservation, platform as string));
  }

  // The canonical platform slug for a reservation's source type, or null when the
  // source doesn't support reviews (so it's excluded from the expected list).
  private reviewPlatformOf(reservation: GuestReservationListItem): string | null {
    const name = reservation.guest_reservation_source_type?.name?.toLowerCase() ?? '';
    return PropertyReviewsService.REVIEWS_SUPPORTED_PLATFORMS.find(p => name.includes(p.match))?.slug ?? null;
  }

  private hasCheckedOut(reservation: GuestReservationListItem, now: number): boolean {
    if (!reservation.check_out_date) {
      return false;
    }
    const checkedOut = new Date(reservation.check_out_date).getTime();
    return !Number.isNaN(checkedOut) && checkedOut < now;
  }

  private toExpectedReview(reservation: GuestReservationListItem, platform: string): ExpectedReview {
    return {
      reservationId: reservation.id,
      listingName: this.resolvePropertyName(reservation.address),
      guestName: this.resolveGuestName(reservation),
      platform,
      checkInDate: reservation.check_in_date || '',
      checkOutDate: reservation.check_out_date || '',
      status: 'needs_review',
    };
  }

  // Reservations carry the guest name under source_data.guests (the booker),
  // not at the top level. Prefer a top-level guest_name if a future payload adds
  // one, then the flagged booker, then the first guest.
  private resolveGuestName(reservation: GuestReservationListItem): string {
    if (reservation.guest_name) {
      return reservation.guest_name;
    }
    const guests = reservation.source_data?.guests || [];
    const booker = guests.find(guest => guest.is_booker) || guests[0];
    return booker?.name || '';
  }

  private resolvePropertyName(address: GuestReservationListItem['address']): string {
    if (!address) {
      return '';
    }
    const name = address.address_name || address.address || address.address1 || '';
    return name && address.zip ? `${name}, ${address.zip}` : name;
  }

  // --- Phase 3: respond to inbound reviews -------------------------------

  // Host reply to a guest's inbound review. gotidy posts it to Airbnb through
  // our browser automation and flips response_status to 'responded'. A platform
  // rejection still answers 2xx with response_status 'failed' + response_error,
  // so callers must check the status, not just that the request succeeded.
  async respondToReview(reviewId: number, responseText: string): Promise<GuestReview> {
    try {
      return await firstValueFrom(
        this.http.post<GuestReview>(
          `${environment.api_url}api/v1/customer/guest-reviews/${reviewId}/respond`,
          { response: responseText }
        )
      );
    } finally {
      this.reviewsChanged.next('inbound');
    }
  }

  // --- Phase 3: leave / queue outbound guest reviews --------------------

  // The live outbound review for a reservation (GET host-guest-reviews
  // ?guest_reservation_id=), so a reopened panel shows the queued/failed/posted
  // review instead of a blank form. null == no review yet.
  async getHostGuestReviewForReservation(guestReservationId: number): Promise<HostGuestReview | null> {
    const params = new HttpParams().set('guest_reservation_id', guestReservationId);
    const reviews = await firstValueFrom(
      this.http.get<HostGuestReview[]>(`${environment.api_url}api/v1/customer/host-guest-reviews`, { params })
    );
    return reviews?.[0] ?? null;
  }

  // Post now (positive) or queue as negative — the backend decides post_at and
  // the resulting status from queue_as_negative. Returns the created record.
  async leaveGuestReview(input: HostGuestReviewInput): Promise<HostGuestReview> {
    return this.changingExpected(firstValueFrom(
      this.http.post<HostGuestReview>(
        `${environment.api_url}api/v1/customer/host-guest-reviews`,
        {
          guest_reservation_id: input.guestReservationId,
          ratings: input.ratings,
          public_text: input.publicText,
          private_note: input.privateNote,
          recommend: input.recommend,
          queue_as_negative: input.queueAsNegative,
        }
      )
    ));
  }

  // Edit a still-pending outbound review before it posts (PUT host-guest-reviews/:id).
  // Only the review content is editable — not the reservation or the queue intent.
  async updateHostGuestReview(id: number, input: HostGuestReviewInput): Promise<HostGuestReview> {
    return this.changingExpected(firstValueFrom(
      this.http.put<HostGuestReview>(
        `${environment.api_url}api/v1/customer/host-guest-reviews/${id}`,
        {
          ratings: input.ratings,
          public_text: input.publicText,
          private_note: input.privateNote,
          recommend: input.recommend,
        }
      )
    ));
  }

  // Re-attempt posting a pending review now (POST host-guest-reviews/:id/post) —
  // the retry for a failed one. Like leave, a platform rejection comes back as
  // status 'failed' with the reason in error.
  async repostHostGuestReview(id: number): Promise<HostGuestReview> {
    return this.changingExpected(firstValueFrom(
      this.http.post<HostGuestReview>(`${environment.api_url}api/v1/customer/host-guest-reviews/${id}/post`, {})
    ));
  }

  // Cancel a pending outbound review; deletes the staged job so it never posts.
  async cancelHostGuestReview(id: number): Promise<void> {
    await this.changingExpected(firstValueFrom(
      this.http.delete<void>(`${environment.api_url}api/v1/customer/host-guest-reviews/${id}`)
    ));
  }

  private async changingExpected<T>(request: Promise<T>): Promise<T> {
    try {
      return await request;
    } finally {
      this.reviewsChanged.next('expected');
    }
  }
}
